import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Container, Card, Button, Spinner, Badge } from 'react-bootstrap';

export default function ConnectedAccounts() {
  // ---------------------------
  // Loading & Reauth states
  // ---------------------------
  const [googleLoading, setGoogleLoading] = useState(false);
  const [ebayLoading, setEbayLoading] = useState(false);
  const [walmartLoading, setWalmartLoading] = useState(false);

  const [googleReauth, setGoogleReauth] = useState(false);
  const [ebayReauth, setEbayReauth] = useState(false);
  const [walmartReauth, setWalmartReauth] = useState(false);

  // ---------------------------
  // Connection statuses
  // ---------------------------
  const [googleStatus, setGoogleStatus] = useState(null);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [walmartStatus, setWalmartStatus] = useState(null);

  // ✅ Google Drive
  const checkGoogleStatus = useCallback(async () => {
    setGoogleLoading(true);
    try {
      const res = await api.get('/google/auth/status');
      setGoogleStatus(res.data?.connected ? 'connected' : 'disconnected');
    } catch (err) {
      console.error('❌ Failed to check Google status:', err);
      setGoogleStatus('disconnected');
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  const handleReauthGoogle = () => {
    setGoogleReauth(true);
    window.location.assign('/api/google/auth/begin');
  };

  // ✅ eBay
const checkEbayStatus = useCallback(async () => {
  setEbayLoading(true);
  try {
    const res = await api.get('/ebay/status');
    setEbayStatus(res.data?.connected ? 'connected' : 'disconnected');
  } catch (err) {
    console.error('❌ Failed to check eBay status:', err);
    setEbayStatus('disconnected');
  } finally {
    setEbayLoading(false);
  }
}, []);

// ✅ Correct eBay OAuth route (now matches your backend)
const handleReauthEbay = () => {
  setEbayReauth(true);

  // open popup for OAuth
  const popup = window.open(
    '/api/ebay', // ✅ <-- this is the working route
    'ebayAuth',
    'width=800,height=700,top=100,left=200'
  );

  if (!popup) {
    alert('Please allow pop-ups for this site to continue eBay authentication.');
    setEbayReauth(false);
    return;
  }

  // monitor popup closing
  const popupTimer = setInterval(() => {
    if (popup.closed) {
      clearInterval(popupTimer);
      setEbayReauth(false);
      checkEbayStatus();
    }
  }, 800);
};

// ✅ Disconnect eBay
const handleDisconnectEbay = async () => {
  if (!window.confirm('Are you sure you want to disconnect your eBay account?')) return;

  try {
    setEbayLoading(true);
    await api.get('/ebay/disconnect');
    alert('eBay account disconnected.');
    await checkEbayStatus();
  } catch (err) {
    console.error('❌ Failed to disconnect eBay:', err);
    alert('Failed to disconnect eBay account.');
  } finally {
    setEbayLoading(false);
  }
};



  // ✅ Walmart
  const checkWalmartStatus = useCallback(async () => {
    setWalmartLoading(true);
    try {
      const res = await api.get('/walmart/status');
      setWalmartStatus(res.data?.connected ? 'connected' : 'disconnected');
    } catch (err) {
      console.error('❌ Failed to check Walmart status:', err);
      setWalmartStatus('disconnected');
    } finally {
      setWalmartLoading(false);
    }
  }, []);

  const handleReauthWalmart = () => {
    setWalmartReauth(true);
    window.location.assign('/api/walmart/debug-token');
  };

  // ---------------------------
  // Initial load
  // ---------------------------
  useEffect(() => {
    checkGoogleStatus();
    checkEbayStatus();
    checkWalmartStatus();
  }, [checkGoogleStatus, checkEbayStatus, checkWalmartStatus]);

  // ---------------------------
  // Listen for popup messages
  // ---------------------------
  useEffect(() => {
    const onMsg = (e) => {
      const type = e?.data?.type;
      if (type === 'google_auth_done' || type === 'EBAY_CONNECTED' || type === 'WALMART_CONNECTED') {
        checkGoogleStatus();
        checkEbayStatus();
        checkWalmartStatus();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [checkGoogleStatus, checkEbayStatus, checkWalmartStatus]);

  // ---------------------------
  // UI Rendering
  // ---------------------------
  return (
    <Container className="mt-4" style={{ maxWidth: 600 }}>
      <h2>Connected Accounts</h2>
      <p>Manage connected integrations and reauthorize when needed.</p>

      {/* Google Drive */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title>Google Drive</Card.Title>
            {googleLoading ? (
              <Spinner animation="border" size="sm" />
            ) : googleStatus === 'connected' ? (
              <Badge bg="success">Connected</Badge>
            ) : (
              <Badge bg="danger">Disconnected</Badge>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              variant={googleStatus === 'connected' ? 'outline-primary' : 'primary'}
              onClick={handleReauthGoogle}
              disabled={googleReauth}
            >
              {googleReauth ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" /> Redirecting…
                </>
              ) : (
                'Reauthorize'
              )}
            </Button>
            <Button variant="outline-secondary" onClick={checkGoogleStatus} disabled={googleLoading}>
              {googleLoading ? 'Checking…' : 'Refresh'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* eBay */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title>eBay</Card.Title>
            {ebayLoading ? (
              <Spinner animation="border" size="sm" />
            ) : ebayStatus === 'connected' ? (
              <Badge bg="success">Connected</Badge>
            ) : (
              <Badge bg="danger">Disconnected</Badge>
            )}
          </div>
          <div className="d-flex gap-2">
  <Button
    variant={ebayStatus === 'connected' ? 'outline-primary' : 'primary'}
    onClick={handleReauthEbay}
    disabled={ebayReauth}
  >
    {ebayReauth ? (
      <>
        <Spinner as="span" animation="border" size="sm" className="me-2" /> Refreshing…
      </>
    ) : (
      'Reauthorize'
    )}
  </Button>

  {ebayStatus === 'connected' && (
    <Button
      variant="outline-danger"
      onClick={handleDisconnectEbay}
      disabled={ebayLoading}
    >
      {ebayLoading ? (
        <>
          <Spinner as="span" animation="border" size="sm" className="me-2" /> Disconnecting…
        </>
      ) : (
        'Disconnect'
      )}
    </Button>
  )}

  <Button
    variant="outline-secondary"
    onClick={checkEbayStatus}
    disabled={ebayLoading}
  >
    {ebayLoading ? 'Checking…' : 'Refresh'}
  </Button>
</div>

        </Card.Body>
      </Card>

      {/* Walmart */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title>Walmart</Card.Title>
            {walmartLoading ? (
              <Spinner animation="border" size="sm" />
            ) : walmartStatus === 'connected' ? (
              <Badge bg="success">Connected</Badge>
            ) : (
              <Badge bg="danger">Disconnected</Badge>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              variant={walmartStatus === 'connected' ? 'outline-primary' : 'primary'}
              onClick={handleReauthWalmart}
              disabled={walmartReauth}
            >
              {walmartReauth ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" /> Redirecting…
                </>
              ) : (
                'Reconnect'
              )}
            </Button>
            <Button variant="outline-secondary" onClick={checkWalmartStatus} disabled={walmartLoading}>
              {walmartLoading ? 'Checking…' : 'Refresh'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
